import { useUpdatePatientJunctionIdMutation } from 'src/participant/patientQueries.generated'
import React from 'react'
import { Autosave, CustomTextField } from 'src/form'
import { Box, Card, CardContent, Grid, Typography } from '@mui/material'
import { Field, Form, Formik } from 'formik'
import { useToasts } from 'react-toast-notifications'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { z } from 'zod'
import { ApolloError } from '@apollo/client'

type Props = {
  uid: string
  junctionID?: string | null
}

const FORM_SCHEMA = z.object({
  junctionID: z.string().optional(),
})

export function IntegrationsForm({ uid, junctionID }: Props) {
  const [update] = useUpdatePatientJunctionIdMutation()
  const { addToast } = useToasts()

  const onSuccess = () => {
    const successMessage = 'Patient junction ID updated successfully'
    const toastOptions = {
      appearance: 'success' as const,
      autoDismissTimeout: 3000,
    }
    addToast(successMessage, toastOptions)
  }

  return (
    <Formik
      validationSchema={toFormikValidationSchema(FORM_SCHEMA)}
      initialValues={{ junctionID }}
      enableReinitialize
      onSubmit={async (values, { resetForm }) => {
        try {
          const trimmedJunctionID = values.junctionID?.trim()
          const result = await update({
            variables: {
              uid,
              junctionID: trimmedJunctionID?.length ? trimmedJunctionID : null,
            },
          })
          if (!result.data?.updatePatientIntegrations) {
            throw new Error('Missing updatePatientIntegrations data')
          }
          resetForm({
            values: {
              junctionID: result.data.updatePatientIntegrations.junctionID,
            },
          })
          onSuccess()
        } catch (e) {
          if (e instanceof ApolloError && e.graphQLErrors[0]?.extensions?.clientError) {
            addToast(e.message, {
              appearance: 'error',
            })
            return
          }
          addToast('Failed to update patient junction ID', {
            appearance: 'error',
          })
        }
      }}
    >
      {({ isSubmitting }) => (
        <Card style={{ marginTop: 10 }}>
          <CardContent>
            <Box mb={1} mt={3}>
              <Typography variant="h3">Integrations</Typography>
            </Box>
            <Form>
              <Autosave debounceMs={300} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Field
                    name="junctionID"
                    label="Junction ID"
                    component={CustomTextField}
                    fullWidth
                    isLoading={isSubmitting}
                  />
                </Grid>
              </Grid>
            </Form>
          </CardContent>
        </Card>
      )}
    </Formik>
  )
}
